import React, { useEffect, useMemo, useRef } from 'react';
import { fetchVectorText } from '@/utils/file-kit';
import { TextFragmentCreator, type TextFragment } from '@/utils/text-kit';
import { type ITextLayerProps } from '@/version-types';
import { useArtText, useTextMark } from '../hooks';
import { type TextLayerProps } from '../types';
import { getBackgroundStyle, getFlipStyle, getFontFamilySubset } from '../utils';
import { ContentRenderer } from './content-renderer';
import { TextLayerContext, type ITextLayerContext } from './context';
import { LinearGradientDef, ShadowDef, TextureDef } from './defs';
import { ListMarkerRenderer } from './list-marker-renderer';

const SVGTextRenderer: React.FC<TextLayerProps> = ({ layer, onRenderEnd }) => {
    const {
        id,
        width,
        height,
        opacity,
        __isOnTextEditing: isOnTextEditing = false,
        __padding,
        __forceRenderKey,
    } = layer;
    const layerData = layer.data as ITextLayerProps;

    const {
        text,
        letterSpacing,
        lineSpacing,
        fontSize,
        writingMode,
        listStyle,
        textAlign,
        verticalAlign,
        textContents = [],
        listStyleMaterial,
    } = layerData;

    const fontFamily = getFontFamilySubset(layer);
    const textContentsStr = JSON.stringify(textContents);

    const listStyleMaterialId = listStyleMaterial?.id;
    const listStyleMaterialScale = listStyleMaterial?.scale;
    const padding = __padding || { top: 0, right: 0, bottom: 0, left: 0 };
    const paddingStr = JSON.stringify(padding);

    const fragments = useMemo<TextFragment[]>(() => {
        return TextFragmentCreator.create(layer);
    }, [
        width,
        height,
        text,
        letterSpacing,
        lineSpacing,
        fontSize,
        fontFamily,
        writingMode,
        listStyle,
        textAlign,
        verticalAlign,
        textContentsStr,
        listStyleMaterialId,
        listStyleMaterialScale,
        paddingStr,
        __forceRenderKey,
    ]);

    const contentFragments = fragments.filter((item) => item.listOrder === undefined);
    const listMarkerFragments = fragments.filter((item) => item.listOrder !== undefined);

    const { markSVG } = useTextMark(layer, contentFragments);
    const { artTextSVG } = useArtText(layer, contentFragments);

    const backgroundStyle = getBackgroundStyle(layer);
    const flipStyle = getFlipStyle(layer);

    const rootStyles = (() => {
        let ret: React.CSSProperties = {
            width,
            height,
            opacity,
        };

        if (!markSVG || markSVG.length === 0) ret = { ...ret, ...backgroundStyle };

        if (!isOnTextEditing) ret = { ...ret, ...flipStyle };

        return ret;
    })();

    const svgContainerStyles: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        overflow: 'visible',
    };
    const viewBox = `0 0 ${width} ${height}`;

    const defIDSuffixRef = useRef<string>(`${Math.random()}`);
    const contextValue: ITextLayerContext = {
        defIDSuffix: defIDSuffixRef.current,
    };

    // [BUGFIX]: https://jira.meitu.com/browse/MTXXPRO-62054
    // 该 bug 产生的原因在于，调用 renderDom 时为同步逻辑，没有等待列表 svg 资源加载完毕
    // 从而导致组件没有渲染出来（`SVGTypedListMarkerRenderer` 内部有判断资源加载情况，未加载则不渲染）
    // 出图阶段需要等待资源加载完毕后再调用 renderDom 返回的 dispose 函数
    useEffect(() => {
        if (!onRenderEnd) return;

        if (listStyleMaterial && listStyleMaterial.url.endsWith('.svg')) {
            fetchVectorText(listStyleMaterial.url).then(() => {
                onRenderEnd();
            });
        }

        onRenderEnd();
    }, [onRenderEnd, listStyleMaterial]);

    return (
        <TextLayerContext.Provider value={contextValue}>
            <div style={rootStyles} data-draw-id={`text-${id}`}>
                {/* 花字背景 svg */}
                {artTextSVG
                    ? artTextSVG.map((svg, idx) => <React.Fragment key={`art-text-${idx}`}>{svg}</React.Fragment>)
                    : null}

                {/* 划重点 svg */}
                {markSVG ? markSVG.map((svg, idx) => <React.Fragment key={`mark-${idx}`}>{svg}</React.Fragment>) : null}

                {/* 文本 svg */}
                <svg
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox={viewBox}
                    style={svgContainerStyles}
                    width={width}
                    height={height}
                >
                    <defs>
                        {/* def: linearGradient 渐变填充 */}
                        <LinearGradientDef layer={layer} />

                        {/* def: texture 纹理填充 */}
                        <TextureDef layer={layer} />

                        {/* def: shadow 阴影特效 */}
                        <ShadowDef layer={layer} />
                    </defs>

                    {/* text content renderer */}
                    <ContentRenderer layer={layer} contentFragments={contentFragments} />

                    {/* list marker fragments */}
                    <ListMarkerRenderer layer={layer} listMarkerFragments={listMarkerFragments} />
                </svg>
            </div>
        </TextLayerContext.Provider>
    );
};

export default SVGTextRenderer;
